import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { walletApi } from "../api/wallet";
import { getStoredTokens, refreshAuthToken } from "../api/interceptor";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";

const WebSocketContext = createContext();

// Notification settings keys
const NOTIFICATION_SETTINGS_KEY = 'notificationSettings';

// Get notification settings from localStorage
const getNotificationSettings = () => {
  try {
    const settings = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
    return settings ? JSON.parse(settings) : {
      telegramMessages: true,
      supportTickets: true,
    };
  } catch {
    return {
      telegramMessages: true,
      supportTickets: true,
    };
  }
};

// Save notification settings to localStorage
const saveNotificationSettings = (settings) => {
  localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
};

export const WebSocketProvider = ({ children }) => {
  const { user, tokens } = useAuth();
  const [socket, setSocket] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [processedNotifications] = useState(new Set()); // Track processed notifications to avoid duplicates
  const [unreadTelegramCount, setUnreadTelegramCount] = useState(0);
  const [notificationSettings, setNotificationSettings] = useState(getNotificationSettings);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isRefreshingSocketTokenRef = useRef(false);

  // Check if user is admin
  const isAdmin = user?.roleId === 1;

  // Update notification settings
  const updateNotificationSettings = useCallback((newSettings) => {
    const updated = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updated);
    saveNotificationSettings(updated);
  }, [notificationSettings]);

  
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const getAccessToken = useCallback(() => {
    const storedTokens = getStoredTokens();
    return storedTokens?.access?.token || tokens?.access?.token || null;
  }, [tokens?.access?.token]);

  const scheduleReconnect = useCallback((delay = 3000) => {
    clearReconnectTimeout();
    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  }, [clearReconnectTimeout]);

  const connectWebSocket = useCallback(() => {
    console.log("Attempting WebSocket connection...");

    const accessToken = getAccessToken();
    if (!user?.id || !accessToken) {
      console.log("Skipping WebSocket connection because auth token is missing");
      return null;
    }
    
    // Close existing connection if any
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log("Closing existing WebSocket connection");
      socketRef.current.__skipReconnect = true;
      socketRef.current.close();
    }
    
    const ws = new WebSocket(
      `${import.meta.env.VITE_WS_URL}?token=${accessToken}`
    );
    socketRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected successfully");
      clearReconnectTimeout();
      isRefreshingSocketTokenRef.current = false;
    };

    ws.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      const data = JSON.parse(event.data);
      if (data.type === "PAYMENT_COMPLETED") {
        setPaymentStatus("completed");
        Swal.fire({
          icon: "success",
          title: "Payment completed successfully!",
          showConfirmButton: true,
          confirmButtonText: "OK",
          confirmButtonColor: "#0E64D1",
          background: "#1e3a8a",
          color: "#fff",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontWeight: "bold",
          textAlign: "center",
          padding: "10px 20px",
          borderRadius: "10px",
        });
        refreshWalletBalance();
      }

      if (data.type === "LOAD_REQUEST_APPROVED") {
        toast.success("Load request approved successfully!");
        refreshWalletBalance();
      }

      if (data.type === "WITHDRAWAL_APPROVED") {
        toast.success("Withdrawal request approved successfully!");
        refreshWalletBalance();
      }

      if (data.type === "MANUAL_LOAD") {
        toast.success(`You received a load of P${data.amount} to your wallet!`);
        refreshWalletBalance();
      }

      if(data.type === "REQUESTER_WALLET_BALANCE"){
        refreshWalletBalance();
      }

      if(data.type === "COMMISSION_TO_WALLET"){
        refreshWalletBalance();
      }

      if(data.type === "WITHDRAWAL_CREATED"){
        refreshWalletBalance();
      }

      // Support ticket notifications
      if (data.type === "NEW_SUPPORT_TICKET") {
        const notificationId = `ticket-${data.data.ticketId}`;
        if (!processedNotifications.has(notificationId)) {
          processedNotifications.add(notificationId);
          toast.success(`New support ticket created: ${data.data.ticketNumber}`, {
            duration: 5000,
            position: 'top-right',
            style: {
              background: '#0E64D1',
              color: '#fff',
            },
          });
        }
      }

      if (data.type === "NEW_SUPPORT_MESSAGE") {
        const notificationId = `message-${data.data.ticketId}-${data.data.messageId}`;
        if (!processedNotifications.has(notificationId)) {
          processedNotifications.add(notificationId);
          const message = data.data.notification || 'New support message';
          
          toast.success(message, {
            duration: 5000,
            position: 'top-right',
            style: {
              background: '#0E64D1',
              color: '#fff',
            },
          });

          // Dispatch custom event to refresh messages in ticket detail
          window.dispatchEvent(new CustomEvent('supportMessageReceived', {
            detail: {
              ticketId: data.data.ticketId,
              messageId: data.data.messageId
            }
          }));
        }
      }

      // Telegram message notifications (admin only)
      if (data.type === "NEW_TELEGRAM_MESSAGE") {
        const notificationId = `telegram-${data.data.sessionId}-${data.data.timestamp}`;
        if (!processedNotifications.has(notificationId)) {
          processedNotifications.add(notificationId);
          
          // Increment unread count
          setUnreadTelegramCount(prev => prev + 1);

          // Get current settings from localStorage (in case they changed)
          const currentSettings = getNotificationSettings();

          // Only send toast notifications if telegram messages are enabled
          if (currentSettings.telegramMessages) {
            const notificationMessage = data.data.notification || 'New Telegram message';
            
            // Show toast notification with dismiss button (in-app only)
            toast((t) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>💬</span>
                <span style={{ flex: 1 }}>{notificationMessage}</span>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  ✕
                </button>
              </div>
            ), {
              duration: 8000,
              position: 'top-right',
              style: {
                background: '#0088cc',
                color: '#fff',
              },
            });
          }

          // Dispatch custom event for TelegramAdminPanel to refresh
          window.dispatchEvent(new CustomEvent('telegramMessageReceived', {
            detail: data.data
          }));
        }
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = async (event) => {
      setSocket((currentSocket) => (currentSocket === ws ? null : currentSocket));

      if (ws.__skipReconnect) {
        return;
      }

      if (event.code === 4001 && !isRefreshingSocketTokenRef.current) {
        console.log("WebSocket token expired. Attempting token refresh before reconnect...");
        isRefreshingSocketTokenRef.current = true;
        const refreshedToken = await refreshAuthToken();
        isRefreshingSocketTokenRef.current = false;

        if (refreshedToken) {
          connectWebSocket();
          return;
        }
      }

      console.log("WebSocket connection closed. Attempting to reconnect...");
      scheduleReconnect(3000);
    };

    setSocket(ws);
    return ws;
  }, [clearReconnectTimeout, getAccessToken, scheduleReconnect, user?.id]);

  useEffect(() => {
    if (user && tokens) {
      console.log("Setting up WebSocket connection...");
      const ws = connectWebSocket();
      return () => {
        console.log("Cleaning up WebSocket connection...");
        clearReconnectTimeout();
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.__skipReconnect = true;
          ws.close();
        }
      };
    }
  }, [clearReconnectTimeout, connectWebSocket, user, tokens]); // Only reconnect if user or token changes

  const refreshWalletBalance = async () => {
    try {
      if (!tokens || !tokens.access || !tokens.access.token) {
        return;
      }
      if (user?.roleId === 10) {
        return;
      }
      const wallet = await walletApi.getWallet(tokens.access.token, user.id);
      
      // Check if we got a redirected response (session expired)
      if (wallet && wallet.redirected === true) {
        console.log('Wallet refresh detected expired token, redirect in progress');
        return null;
      }
      
      setWalletBalance(wallet.balance);
      return wallet.balance;
    } catch (error) {
      console.error("Error refreshing wallet:", error);
      return null; // Return null but don't rethrow to prevent UI issues
    }
  };

  // Function to reset payment status
  const resetPaymentStatus = () => {
    setPaymentStatus(null);
  };

  // Function to clear unread Telegram count
  const clearUnreadTelegramCount = () => {
    setUnreadTelegramCount(0);
  };

  useEffect(() => {
    if (user && tokens) {
      refreshWalletBalance();
    }
  }, [user, tokens]);

  useEffect(() => {
    return () => {
      clearReconnectTimeout();
      if (socketRef.current) {
        socketRef.current.__skipReconnect = true;
        socketRef.current.close();
      }
    };
  }, [clearReconnectTimeout]);

  return (
    <WebSocketContext.Provider value={{ 
      paymentStatus, 
      refreshWalletBalance,
      walletBalance,
      resetPaymentStatus,
      unreadTelegramCount,
      clearUnreadTelegramCount,
      isAdmin,
      // Notification settings (toast only)
      notificationSettings,
      updateNotificationSettings,
    }}>
      {children}
      <Toaster position="bottom-center" reverseOrder={false} />
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
